import { BREAKPOINTS } from "../config/breakpoints";
import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import styled from "styled-components";
import LandingConnector from "../Landing/Connector/LandingConnector";
import { CSSTransition } from "react-transition-group";
import useWindowSize from "../util/hooks/useWindowSize";
import { zIndex } from "../config/zIndex";

const Bar = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	height: 100%;
	overflow-y: auto;
	overflow-x: hidden;
	z-index: ${zIndex.sidebar};
	transition: 300ms all ease-in-out;
	width: 18.5rem;
	background-color: #fff;
	opacity: ${(props) => props.opacity};
	pointer-events: ${(props) => props.pointerEvents};
	transform: ${(props) => props.transform};
	-ms-overflow-style: none;
	scrollbar-width: none;
	&::-webkit-scrollbar {
		display: none;
	}
	@media (max-width: ${BREAKPOINTS.tablet - 0.02}px) {
		position: fixed;
		top: 4.5rem;
		left: 0;
		height: calc(100vh - 4.5rem);
		width: min(20rem, 88vw);
		border-top: 1px solid rgba(0, 0, 0, 0.12);
		box-shadow: rgba(0, 0, 0, 0.25) 2px 0 10px 0;
	}
`;

const Backdrop = styled.div`
	position: fixed;
	top: 4.5rem;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.55);
	z-index: ${zIndex.sidebar - 1};
	@media (min-width: ${BREAKPOINTS.tablet}px) {
		display: none;
	}
`;

const SubBar = styled.div`
	display: flex;
	flex-direction: column;
`;

const Item = styled.div`
	display: flex;
	justify-content: ${(props) => props.jContent};
	align-items: center;
	padding: 0.8rem 0.5rem 0.8rem 1rem;
	margin: 0 0.5rem;
	border-radius: 7px;
	cursor: pointer;
	font-weight: 500;
	background: ${(props) => (props.isActive ? "rgba(45, 35, 66, 0.1)" : "")};
	transition: background 200ms, color 200ms;
`;

Item.defaultProps = {
	jContent: "space-between",
};

const SubItem = styled.div`
	display: flex;
	align-items: center;
	padding: 1rem;
	margin: 0 0.5rem 0 2.5rem;
	border-radius: 7px;
	cursor: pointer;
	background: ${(props) => (props.isActive ? "rgba(45, 35, 66, 0.1)" : "")};
	transition: background 200ms, color 200ms;
`;

const Div = styled.div`
	color: ${(props) => props.color};
	background: ${(props) => props.background};
	height: ${(props) => props.height};
	width: ${(props) => props.width};
	display: flex;
	flex-direction: ${(props) => props.flexDirection};
	align-items: ${(props) => props.alignItems};
	justify-content: ${(props) => props.jContent};
	flex-wrap: ${(props) => props.flexWrap};
	margin: ${(props) => props.margin};
	padding: ${(props) => props.padding};
	font-size: ${(props) => props.fontSize};
	font-weight: ${(props) => props.fontWeight};
	box-shadow: ${(props) => props.boxShadow};
	border-radius: ${(props) => props.borderRadius};
	align-self: ${(props) => props.alignSelf};
	border: ${(props) => props.border};
`;

Div.defaultProps = {
	alignItems: "center",
	jContent: "center",
	width: "100%",
};

const Sidebar = (props) => {

	const { config, showSidebar, toggle } = props;
	const [width] = useWindowSize();
	const isMobile = width < BREAKPOINTS.tablet;

	const drawerOpen = !isMobile || Boolean(showSidebar);
	const drawerTransform = isMobile && !showSidebar ? "translateX(-100%)" : "translateX(0)";

	useEffect(() => {
		if (isMobile && showSidebar) {
			document.body.style.overflow = "hidden";
			return () => {
				document.body.style.overflow = "";
			};
		}
	}, [isMobile, showSidebar]);

	const [openTabs, updateOpenTabs] = useState([]);

	function toggleTab(tab) {
		updateOpenTabs((prev) => {
			if (prev.includes(tab)) {
				return prev.filter((activeTab) => activeTab !== tab);
			} else {
				return [...prev, tab];
			}
		});
	}

	function renderSidebarLinks(config) {
		let renderData = [];
		for (const item in config) {
			const { title, link, icon, isMaterialIcon, hasSubLinks, subLinks } = config[item];
			const isExpanded = openTabs.includes(item);
			if (hasSubLinks) {
				let subLinkRenderData = [];

				for (const subLink of subLinks) {
					const {
						title: subLinkTitle,
						route: subLinkRoute,
						link: subLinkLink,
						icon: subLinkIcon,
						isMaterialIcon: isMaterialIconSublink,
					} = subLink;

					const subLinkPointsTo = subLinkRoute?.path ?? subLinkLink;
					subLinkRenderData.push(
						<NavLink to={subLinkPointsTo} onClick={() => isMobile && props.toggle(false)} end >
							{({ isActive }) => (
								<SubItem jContent="" isActive={isActive} >
									{isMaterialIconSublink ? <span className="material-symbols-outlined">{subLinkIcon}</span> : subLinkIcon}
									&nbsp; {subLinkTitle}
								</SubItem>
							)}
						</NavLink>
					);
				}

				renderData.push(
					<Item
						onClick={() => {
							toggleTab(item);
						}}>
						<div style={{ display: "flex", alignItems: "center" }}>
							{isMaterialIcon ? <span className="material-symbols-outlined">{icon}</span> : icon}
							&nbsp; {title}
						</div>
						<div className={isExpanded ? "rotate-up" : "rotate-down"}>
							<span className="material-symbols-outlined">expand_more</span>
						</div>
					</Item>
				);

				renderData.push(
					<CSSTransition in={isExpanded} unmountOnExit classNames="dropdown" timeout={1000}>
						<div>{subLinkRenderData}</div>
					</CSSTransition>
				);
			} else {
				renderData.push(
					<NavLink to={link} onClick={() => isMobile && props.toggle(false)}>
						{({ isActive }) => (
							<Item jContent="" isActive={isActive} >
								{isMaterialIcon ? <span className="material-symbols-outlined">{icon}</span> : icon}
								&nbsp; {title}
							</Item>
						)}
					</NavLink>
				);
			}
		}
		return renderData;
	}

	return (
		<>
			{isMobile && showSidebar ? <Backdrop onClick={() => toggle(false)} /> : null}
			<Bar
				opacity={drawerOpen ? "1" : "0"}
				transform={drawerTransform}
				pointerEvents={drawerOpen ? "all" : "none"}>
				<SubBar>
					{renderSidebarLinks(config)}
				</SubBar>
			</Bar>
		</>
	);
};

export default LandingConnector(Sidebar);
